<template>
    <div>
        <b-modal
            id="treatementStart"
            ref="treatementStart"
            :lazy="true"
            :scrollable="true"
            :title="title"
            title-tag="h2"
            @shown="shown"
            @hidden="hidden"
            @hide="hide"
        >
            <transition name="slide-fade" id="treatementStartTransition">
                <b-form v-if="page === 'start'">
                    <b-alert
                        show
                        variant="warning"
                        v-if="data.isDefaultProvider && data.template_id != 0"
                    >
                        <span id="dialog-alert-message">
                            {{ CLINIC_PROVIDER_ALERT.LAB_RADIOLOGY_BUNDLE }}
                        </span>
                    </b-alert>
                    <b-form-group
                        :label-for="`template_id`"
                        :label-cols="3"
                        label="Template:"
                    >
                        <v-select
                            v-if="loadingOption.bundle"
                            :loading="true"
                            :options="[]"
                            class="show-actions"
                            placeholder="No Bundle"
                        ></v-select>
                        <b-form-select
                            v-else
                            id="template_id"
                            v-model="data.template_id"
                            :options="bundleFilter"
                            name="template_id"
                        ></b-form-select>
                    </b-form-group>
                    <b-form-group
                        label="Triage:"
                        class
                        label-cols="3"
                        label-for="triage_type_id"
                    >
                        <v-select
                            v-if="loadingOption.triage"
                            :loading="true"
                            :options="[]"
                            class="show-actions"
                            placeholder="Loading"
                        ></v-select>
                        <b-form-select
                            v-else
                            id="triage_type_id"
                            v-model="data.triage_type_id"
                            class="form-control"
                            name="triage_type_id"
                        >
                            <option
                                v-for="triage in triageOptions"
                                :key="triage.id"
                                :value="triage.id"
                                >{{ triage.triage }}</option
                            >
                        </b-form-select>
                    </b-form-group>
                    <b-form-group
                        label-for="time"
                        :label-cols="3"
                        label="Start:"
                    >
                        <timepicker
                            v-model="data.time"
                            name="time"
                            class="form-control"
                        />
                    </b-form-group>
                    <b-form-group
                        :label-for="`room`"
                        :label-cols="3"
                        label="Room:"
                    >
                        <v-select
                            v-if="loadingOption.room"
                            :loading="true"
                            :options="[]"
                            class="show-actions"
                            placeholder="Select room (optional)"
                        ></v-select>
                        <b-form-select
                            v-else
                            id="room"
                            v-model="data.room"
                            :options="rooms"
                            name="room"
                        ></b-form-select>
                    </b-form-group>
                    <b-form-group
                        :label-for="`staffs`"
                        :label-cols="3"
                        label="Assigned To:"
                    >
                        <v-select
                            id="staffs"
                            ref="vselect"
                            v-model="data.staffs"
                            v-validate="'required'"
                            class="show-actions"
                            :class="{ 'border-danger': errors.has('staffs') }"
                            :options="staff_options_exclude"
                            :label="'fullname'"
                            name="staffs"
                            multiple
                            aria-placeholder="true"
                            placeholder="Assigned to (required)"
                            :loading="loadingOption.assign"
                        ></v-select>
                    </b-form-group>
                    <b-form-group
                        label-for="soap_id"
                        :label-cols="3"
                        label="Medical Note:"
                    >
                        <v-select
                            v-if="loadingOption.soap"
                            :loading="true"
                            :options="[]"
                            class="show-actions"
                            placeholder="Do not create medical note"
                        ></v-select>
                        <b-form-select
                            v-else-if="!soap"
                            id="soap_id"
                            v-model="data.soap"
                            :options="sortedSoaps"
                            name="soap_id"
                        ></b-form-select>
                        <p v-else class="plain-text">
                            <a :href="'../apps/' + soap.ci_url">
                                {{ soap.label }}
                            </a>
                        </p>
                    </b-form-group>
                    <b-form-group
                        label="Billing:"
                        label-cols="3"
                        label-for="bill_to_invoice"
                    >
                        <v-select
                            v-if="loadingOption.billing"
                            :loading="true"
                            :options="[]"
                            class="show-actions"
                            :placeholder="`Select invoice or estimate (${
                                data.template_id ? 'required' : 'optional'
                            })`"
                        ></v-select>

                        <b-form-select
                            v-else
                            id="bill_to_invoice"
                            v-model="data.bill_to_invoice"
                            v-validate="data.template_id ? 'excluded:-3' : ''"
                            :class="{
                                'border-danger': errors.has('bill_to_invoice')
                            }"
                            name="bill_to_invoice"
                            class="form-control"
                        >
                            <option v-if="data.template_id" :value="-3"
                                >Select invoice or estimate (required)</option
                            >
                            <option v-else :value="-3"
                                >Select invoice or estimate (optional)</option
                            >
                            <option
                                v-for="bill in billings"
                                :key="bill.id"
                                :value="bill"
                                >{{ billingNote(bill) }}</option
                            >
                            <option :value="-1">New Invoice</option>
                            <option :value="-2">New Estimate</option>
                        </b-form-select>
                    </b-form-group>
                </b-form>
                <b-form v-else-if="page === 'activity'">
                    <b-row class="w-100 ml-0" align-v="start">
                        <div>
                            <b-form-checkbox
                                v-model="selectedAll"
                                class="align-items-start"
                                plain
                                :disabled="!_.some(templates)"
                                @change="selectAll"
                            >
                                <strong>Select / Unselect All</strong>
                            </b-form-checkbox>
                        </div>
                    </b-row>
                    <hr class="mb-0" />
                    <item-rows
                        v-for="template in templates"
                        :key="template.id"
                        :element="template"
                        :patient-id="data.params.patient.id"
                        :templates="templates"
                        :isUpdatePosition="true"
                        :parentRef="'treatementStart'"
                        @update-selected-all="updateSelectedAll"
                        @update-new-templates="
                            newTemplates => (templates = newTemplates)
                        "
                    />
                    <div v-if="!_.some(templates)" class="pt-3">
                        <em>No activity defined in the template</em>
                    </div>
                    <hr v-if="!_.some(templates)" class="mb-0" />
                </b-form>
                <b-form v-else-if="page === 'bundle'">
                    <bundle
                        ref="bundle"
                        v-if="page == 'bundle'"
                        :data="bundle_data"
                        :app-data="data"
                        @loading="loading = true"
                        @loaded="bundleLoaded"
                        class="px-1"
                    />
                </b-form>
            </transition>
            <div slot="modal-footer" class="w-100">
                <b-button
                    v-if="!loading"
                    class="float-left"
                    variant="secondary"
                    @click="$refs.treatementStart.hide('cancel')"
                    >Cancel</b-button
                >
                <div class="float-right">
                    <img v-if="loading" src="assets/ajax-loader.gif" />
                    <div v-if="!loading">
                        <b-button
                            v-if="page !== 'start'"
                            class="float-left mr-2"
                            variant="primary"
                            @click="back"
                            >Back</b-button
                        >
                        <b-button
                            v-if="data.template_id !== '0' && page === 'start'"
                            class="float-left"
                            variant="primary"
                            @click="next"
                            >Next</b-button
                        >
                        <b-button
                            v-if="
                                data.template_id !== '0' &&
                                page === 'activity' &&
                                !saveButton
                            "
                            class="float-left"
                            variant="primary"
                            @click="nextBundle"
                            >Next</b-button
                        >
                        <b-button
                            v-if="
                                data.template_id === '0' ||
                                page === 'bundle' ||
                                saveButton
                            "
                            class="float-left"
                            variant="primary"
                            @click="save"
                            >Save</b-button
                        >
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>
<style></style>
<script>
import uuidv4 from "uuid/v4";
import ScheduleHelper from "../helper";
import Bundle from "@/module/commons/bundle/bundle";
import vSelect from "vue-select";
import Timepicker from "@/module/commons/time-picker";
import { DateTime } from "luxon";
import ItemRows from "./template-item";
import LabMixin from "@/mixins/lab";
import {
    cloneDeep,
    difference,
    some,
    get,
    set,
    union,
    map,
    assign,
    sortBy,
    orderBy,
    find,
    size,
    times,
    filter
} from "lodash";
import CLINICPROVIDER from "@/constants/clinic-provider";
import TriageService from "@/services/triage";

// Constant for readability
const DUE_AT_SPECIFIC_TIME = 2;

export default {
    name: "StartActivity",
    components: {
        vSelect,
        Timepicker,
        ItemRows,
        Bundle
    },
    mixins: [ScheduleHelper, LabMixin],
    data() {
        return {
            data: {
                patient: null,
                time: DateTime.local(),
                actions: [],
                template_id: "0",
                room: null,
                soap: null,
                bill_to_invoice: -3,
                staffs: [],
                provider_id: "",
                params: null,
                isDefaultProvider: false,
                triage_type_id: 5
            },
            loadingOption: {
                room: false,
                bundle: false,
                assign: false,
                billing: false,
                soap: false,
                triage: false
            },
            bundle_data: {
                bundle_id: null,
                selectedElements: [],
                elements: [],
                module: ""
            },
            datePickerOptions: {
                format: "DD/MM/YYYY"
            },
            basic_soap: [
                { value: "a", text: "Basic SOAP" },
                { value: "b", text: "Simple SOAP" },
                { value: "c", text: "Standard SOAP" }
            ],
            title: "Start Treatment",
            loading: false,
            saveButton: false,
            checker: true,
            bundles: [],
            actionsList: [],
            soaps: [],
            soap: null,
            invoices: [],
            staffList: [],
            estimates: [],
            rooms: [{ value: null, text: "Select room (optional)" }],
            page: "start",
            templates: [],
            selectedAll: true,
            bundleRecordId: "",
            bundleInvoiceId: "",
            bundleInvoiceItemId: [],
            check_in_billing: "",
            providers: [],
            type: [
                {
                    value: null,
                    text: "Select type of activity (required)"
                },
                {
                    value: 1,
                    text: "Medical"
                },
                {
                    value: 2,
                    text: "Vitals"
                },
                {
                    value: 3,
                    text: "Other"
                }
            ],
            abortController: {
                bundle: null,
                room: null,
                staff: null,
                provider: null,
                soap: null,
                estimate: null,
                invoice: null,
                appointment: null
            },
            CLINIC_PROVIDER_ALERT: CLINICPROVIDER.CLINIC_PROVIDER_ALERT,
            triageOptions: []
        };
    },
    computed: {
        // TODO: will be moved to helper component soon
        actions_options_exclude() {
            return difference(this.actionsList, this.data.actions);
        },
        staff_options_exclude() {
            return difference(this.staffList, this.data.staffs);
        },
        bundleFilter() {
            return [
                { value: "0", text: "No Bundle" },
                ...this.bundles.map(bundle => ({
                    value: bundle.id,
                    text: bundle.name
                }))
            ];
        },
        billings() {
            return union(
                map(this.invoices, array => assign({}, array, { type: -1 })),
                map(this.estimates, array => assign({}, array, { type: -2 }))
            );
        },
        sortedSoaps() {
            return [
                { value: null, text: "Do not create medical note" },
                ...sortBy(this.soaps, ["text"])
            ];
        },
        labelOptions() {
            return this.$store.state.reference["treatment-task-label"];
        },
        isRelatedToTreatmentBoard() {
            return (
                this.$route.name === "appointment" ||
                this.$route.name === "census"
            );
        }
    },
    watch: {
        "data.staffs"(value) {
            if (value !== "") {
                let defaultClinicProvider = find(value, [
                    "is_default_provider",
                    true
                ]);
                this.data.isDefaultProvider = Boolean(defaultClinicProvider);
            }
        }
    },
    mounted() {},
    methods: {
        open(params) {
            let self = this;
            self.data.params = params;
            self.$refs["treatementStart"].show();
            if (
                !self.data.bill_to_invoice ||
                self.data.bill_to_invoice === -3
            ) {
                switch (self.$root.setting.check_in_billing) {
                    case 1:
                        self.data.bill_to_invoice = -2;
                        break;
                    case 2:
                        self.data.bill_to_invoice = -1;
                        break;
                    case 3:
                        self.check_in_billing =
                            self.$root.setting.check_in_billing;
                        break;
                    default:
                        self.data.bill_to_invoice = -3;
                        break;
                }
            }
        },
        shown() {
            this.data.time = DateTime.local().setZone(this._timeZone);
            this.loading = true;
            this.bundles = [];
            this.rooms = [];
            this.providers = [];
            this.staffList = [];
            this.estimates = [];
            this.invoices = [];
            this.soaps = [];
            this.triageOptions = [];

            Promise.all([
                this.getStaffList(),
                this.getProvider(),
                this.getBundle(),
                this.getRooms(),
                this.getTriageList(),
                this.data.params.treatment.appointment_id
                    ? this.getAppointment(
                          this.data.params.treatment.appointment_id
                      ).then(appointment => {
                          if (appointment) {
                              this.data.provider_id = appointment.provider.id;
                              if (appointment.soap) {
                                  this.soap = appointment.soap;
                              } else {
                                  this.getSoap();
                              }
                          }
                      })
                    : this.getSoap(),
                this.getBilling(this.data.params)
            ]).finally(() => {
                this.loading = false;
                this.data.template_id = this.data.params.treatment.template_id
                    ? this.data.params.treatment.template_id
                    : "0";
            });
        },
        hidden() {},
        async hide() {
            // abortcontroller
            this.abortController.staff.abort();
            this.abortController.bundle.abort();
            this.abortController.room.abort();
            this.abortController.provider.abort();
            this.abortController.estimate.abort();
            this.abortController.invoice.abort();
            if (this.abortController.soap) {
                this.abortController.soap.abort();
            }
            if (this.abortController.appointment) {
                this.abortController.appointment.abort();
            }
        },
        selectAll() {
            this.templates = this.templates.map(template => {
                if (some(template.medications)) {
                    if (!this.selectedAll) {
                        if (template.originalQuantity) {
                            template.medications[0].quantity =
                                template.originalQuantity;
                        }
                    } else {
                        template.originalQuantity =
                            template.medications[0].quantity;
                        template.medications[0].quantity = 0;
                    }
                }
                return {
                    ...template,
                    selected: !this.selectedAll
                };
            });
        },
        updateSelectedAll() {
            this.selectedAll = !this.templates.find(
                template => !template.selected
            );
        },
        async next() {
            let valid = await this.$validator.validateAll();
            if (valid) {
                $(".modal-content").removeAttr("tabindex");

                this.templates = [];
                this.loading = true;
                await this.getActivities();
                this.bundleChecker();
            }
        },
        nextBundle() {
            let self = this;
            self.checker = false;
            self.bundle_data.bundle_id = this.data.template_id;
            self.bundle_data.module = "treatmentboard";

            self.page = "bundle";
            self.title = "New Bundle";

            $(".modal-content").removeAttr("tabindex");
        },
        back() {
            this.saveButton = false;
            if (this.page === "bundle") {
                if (!_.some(this.templates)) {
                    this.page = "start";
                    this.title = "Add Bundle";
                } else {
                    this.page = "activity";
                    this.title = "New Activity";
                }
            } else if (this.page === "activity") {
                this.page = "start";
                this.title = "Start Treatment";
            }
        },
        bundleChecker() {
            let self = this;
            self.checker = true;
            self.bundle_data.bundle_id = this.data.template_id;
            self.bundle_data.module = "treatmentboard";
            self.page = "bundle";
        },
        bundleLoaded(value) {
            let self = this;
            self.loading = false;
            if (this.checker) {
                if (value) {
                    if (this.templates.length == 0) {
                        this.nextBundle();
                    } else {
                        this.page = "activity";
                        this.title = "New Activity";
                        this.saveButton = false;
                        $(".modal-content").removeAttr("tabindex");
                    }
                } else {
                    this.page = "activity";
                    this.title = "New Activity";
                    this.saveButton = true;
                    $(".modal-content").removeAttr("tabindex");
                }
            }
        },
        setDefault() {
            let self = this;
            self.data.template_id = 0;
            self.data.room = 0;
        },
        async getAppointment(id) {
            // to enable the abortController, not sure why cannot apply the abortController from helper.js
            this.abortController.appointment = new AbortController();
            const response = await this.$http.get(
                process.env.SCHEDULE_SERVICE + "appointment/" + id,
                { signal: this.abortController.appointment.signal }
            );
            if (response) {
                let data = {};
                data = response.data.response.resources;
                data.start = moment(data.start).tz(this._timeZone);
                data.end = moment(data.end).tz(this._timeZone);
                return data;
            }
            return null;
        },
        getProvider() {
            let self = this;

            self.abortController.provider = new AbortController();
            return this.$http
                .get(
                    process.env.APPS +
                        "index.php/ajax/staff/filter_administrator_veterinarian_sql?search_key=",
                    { signal: self.abortController.provider.signal }
                )
                .then(function (response) {
                    if (response && response.data) {
                        self.providers = response.data;
                        self.providers = _.sortBy(self.providers, ["name"]);
                    }
                });
        },
        getStaffList() {
            let self = this;
            self.loadingOption.assign = true;
            self.abortController.staff = new AbortController();
            return this.$http
                .get(
                    process.env.ACTOR_SERVICE +
                        "staff?provider=1&exclude_default",
                    {
                        signal: self.abortController.staff.signal
                    }
                )
                .then(function (response) {
                    self.loadingOption.assign = false;
                    if (!response) {
                        // when abort, response become undefined
                        return [];
                    }
                    if (response.data.response.resources) {
                        self.staffList = response.data.response.resources;
                    }

                    self.data.staffs = [];
                    self.data.params.treatment.staffs.forEach(staff => {
                        const staffFound = find(self.staffList, {
                            id: staff.id
                        });
                        if (staffFound) {
                            self.data.staffs.push(staffFound);
                        }
                    });

                    self.$validator.reset();
                    return self.staffList;
                });
        },
        getBundle() {
            let self = this;
            self.loadingOption.bundle = true;
            self.abortController.bundle = new AbortController();

            return this.$http
                .get(
                    process.env.ACCOUNT_SERVICE + "template/bundle?allBundle=1",
                    { signal: this.abortController.bundle.signal }
                )
                .then(function (response) {
                    self.loadingOption.bundle = false;
                    if (
                        response &&
                        response.data.response.message.code == 200
                    ) {
                        self.bundles = response.data.response.resources;
                    }
                });
        },
        getRooms() {
            let self = this;
            self.loadingOption.room = true;
            self.abortController.room = new AbortController();
            return this.$http
                .get(process.env.SCHEDULE_SERVICE + "rooms", {
                    signal: self.abortController.room.signal
                })
                .then(function (response) {
                    self.loadingOption.room = false;
                    if (response && response.data.response.resources) {
                        self.rooms = [
                            { value: null, text: "Select room (optional)" },
                            ...response.data.response.resources.map(room => ({
                                value: room.id,
                                text: room.name
                            }))
                        ];
                        self.data.room = self.data.params.treatment.rooms.length
                            ? self.data.params.treatment.rooms[0].id
                            : null;
                    }
                });
        },
        getSoap() {
            let self = this;
            self.loadingOption.soap = true;
            self.abortController.soap = new AbortController();
            return this.$http
                .get(process.env.ACCOUNT_SERVICE + "template/soap", {
                    signal: this.abortController.soap.signal
                })
                .then(response => {
                    self.loadingOption.soap = false;
                    if (response) {
                        this.soaps = response.data.response.resources.map(
                            soap => ({
                                value: soap.id,
                                text: soap.name
                            })
                        );
                        this.data.soap = find(this.soaps, {
                            id: this.data.params.treatment.soap_id
                        })
                            ? this.data.params.treatment.soap_id
                            : null;
                    }
                });
        },
        getActivities() {
            return this.$http
                .get(
                    process.env.BARRACUDA +
                        "schedule/treatment-template-activities/" +
                        this.data.template_id
                )
                .then(response => {
                    if (response.data.response.resources) {
                        this.templates = this.generateActivities(
                            response.data.response.resources
                        );
                    }
                    this.loading = false;
                    return this.templates;
                });
        },
        generateActivities(templates) {
            let occurence_array = [];
            let labelOptions = this.labelOptions;

            templates.forEach(template => {
                if (get(template, "activity.repeat.occurence") > 1) {
                    template.id = uuidv4();
                    template.occurrence_id = template.id;
                    times(get(template, "activity.repeat.occurence"), time => {
                        if (time < 1) {
                            return;
                        }
                        let interval = get(
                            template,
                            "activity.repeat.specific_interval"
                        );
                        let object = cloneDeep(template);

                        // -- assign temporary uniqid
                        object.id = uuidv4();
                        if (template.activity.due.type === 1) {
                            set(
                                object,
                                "activity.due.after_start_time",
                                get(template, "activity.due.after_start_time") +
                                    interval * time
                            );
                        } else {
                            set(
                                object,
                                "activity.due.specific_time",
                                DateTime.fromFormat(
                                    get(template, "activity.due.specific_time"),
                                    "hh:mm a"
                                )
                                    .plus({
                                        minutes: interval * time
                                    })
                                    .toSeconds()
                            );
                        }
                        occurence_array.push(object);
                    });
                }
            });
            return orderBy(
                this.addOneDay(
                    union(templates, occurence_array).map(template => ({
                        id: template.id,
                        occurrence_id: template.occurrence_id,
                        selected: true,
                        treatment_board_id: this.data.params.treatmentID,
                        treatment_activity_type_id: template.activity_type,
                        repeat_data: template.activity.repeat,
                        repeat: template.activity.repeat,
                        due: this._calculateDue(
                            this.data.time,
                            template.activity.due.type === 1
                                ? template.activity.due.after_start_time
                                : template.activity.due.specific_time,
                            template.activity.due.type,
                            DateTime.local()
                        ).diff,
                        due_type: template.activity.due.type,
                        time: this._calculateDue(
                            this.data.time,
                            template.activity.due.type === 1
                                ? template.activity.due.after_start_time
                                : template.activity.due.specific_time,
                            template.activity.due.type,
                            DateTime.local()
                        ).time,
                        note: "",
                        medications: template.activity.item_id
                            ? [
                                  {
                                      item_id: template.item.id,
                                      name: template.item.name,
                                      uom: template.item.uom,
                                      status: 0,
                                      quantity: template.activity.quantity,
                                      route_id: template.activity.route_id,
                                      location_id:
                                          template.activity.location_id,
                                      template_element_id:
                                          template.templateElement[0].id
                                  }
                              ]
                            : undefined,
                        otherTasks: [
                            {
                                label_id: get(template, "activity.label_id", 1),
                                taskType:
                                    template.activity_type === 3
                                        ? find(labelOptions, {
                                              value: parseInt(
                                                  template.activity.label_id
                                              )
                                          }).text
                                        : null,
                                task: template.activity.task,
                                status: 0
                            }
                        ],
                        vitals: template.master_vital
                            ? template.master_vital.map(item => {
                                  let imperial = this._setting.weights_measures;
                                  let masterUom = [];
                                  switch (parseInt(item.id)) {
                                      case 3:
                                          masterUom = find(item.master_uom, {
                                              uom: "F"
                                          });
                                          if (imperial === 2)
                                              masterUom = find(
                                                  item.master_uom,
                                                  {
                                                      uom: "C"
                                                  }
                                              );
                                          break;
                                      case 4:
                                          masterUom = find(item.master_uom, {
                                              uom: "Lbs"
                                          });
                                          if (imperial === 2)
                                              masterUom = find(
                                                  item.master_uom,
                                                  {
                                                      uom: "Kg"
                                                  }
                                              );
                                          break;
                                      default:
                                          masterUom = item.master_uom[0];
                                          break;
                                  }

                                  return {
                                      master_vital_id: item.id,
                                      name: item.name,
                                      value: null,
                                      patient_id: this.data.params.patient.id,
                                      master_uom_id: masterUom
                                          ? masterUom.id
                                          : 0,
                                      status: 0
                                  };
                              })
                            : undefined
                    }))
                ),
                "time",
                "asc"
            );
        },
        /**
         * Ensure every activity’s `due` / `time` is not before `now`.
         * If the first occurrence in a chain moves, its children move too.
         * Mutates the objects in-place and also returns the array for chaining.
         */
        addOneDay(activities) {
            const tz = this._timeZone; // grab once
            const now = this._now(); // Luxon DateTime in the same zone
            const bumpedSet = new Set(); // parents that were shifted

            // helper: return epoch, shifted +1 day if in the past
            const shiftIfPast = epoch => {
                const dt = DateTime.fromSeconds(epoch).setZone(tz);
                return dt < now ? dt.plus({ days: 1 }).toSeconds() : epoch;
            };

            return activities.map(act => {
                const isParent = act.id === act.occurrence_id;
                const parentBump = bumpedSet.has(act.occurrence_id);

                //------------------------------------------
                // Case A – This is the parent in the chain
                //------------------------------------------
                if (act.occurrence_id && isParent) {
                    const oldDue = act.due;
                    const oldTime = act.time;

                    if (act.due_type === DUE_AT_SPECIFIC_TIME) {
                        act.due = shiftIfPast(act.due);
                    }
                    act.time = shiftIfPast(act.time);

                    // Record that this chain must be shifted
                    if (oldDue !== act.due || oldTime !== act.time) {
                        bumpedSet.add(act.occurrence_id);
                    }
                    return act;
                }

                //------------------------------------------------------
                // Case B – Child occurrence whose parent was bumped
                //------------------------------------------------------
                if (act.occurrence_id && parentBump) {
                    if (act.due_type === DUE_AT_SPECIFIC_TIME) {
                        act.due = DateTime.fromSeconds(act.due)
                            .setZone(tz)
                            .plus({ days: 1 })
                            .toSeconds();
                    }
                    act.time = DateTime.fromSeconds(act.time)
                        .setZone(tz)
                        .plus({ days: 1 })
                        .toSeconds();
                    return act;
                }

                //------------------------------------------
                // Case C – Not recurring OR parent untouched
                //------------------------------------------
                if (act.due_type === DUE_AT_SPECIFIC_TIME) {
                    act.due = shiftIfPast(act.due);
                }
                act.time = shiftIfPast(act.time);
                return act;
            });
        },
        getBilling(params) {
            let self = this;
            self.loadingOption.billing = true;
            self.abortController.estimate = new AbortController();
            self.abortController.invoice = new AbortController();
            return Promise.all([
                this.$http
                    .get(
                        process.env.ACTOR_SERVICE +
                            "client/" +
                            params.clientID +
                            "/estimate",
                        { signal: this.abortController.estimate.signal }
                    )
                    .then(response => {
                        if (response) {
                            this.estimates = response.data.response.resource;
                        }
                    }),

                this.$http
                    .get(
                        process.env.ACTOR_SERVICE +
                            "client/" +
                            params.clientID +
                            "/invoice",
                        { signal: self.abortController.invoice.signal }
                    )
                    .then(response => {
                        if (response) {
                            this.invoices = response.data.response.resource;
                        }
                    })
            ]).then(() => {
                return this.$nextTick(() => {
                    self.loadingOption.billing = false;
                    if (
                        (this.data.params.treatment.invoice_id &&
                            this.data.params.treatment.invoice_id !== "-1") ||
                        (this.data.params.treatment.estimate_id &&
                            this.data.params.treatment.estimate_id !== "-1")
                    ) {
                        let bill_id = this.data.params.treatment.invoice_id
                            ? this.data.params.treatment.invoice_id
                            : this.data.params.treatment.estimate_id;
                        this.data.bill_to_invoice =
                            find(this.billings, {
                                id: bill_id
                            }) || this.data.bill_to_invoice;
                    } else if (this.data.params.treatment.appointment_id) {
                        return this.getAppointment(
                            this.data.params.treatment.appointment_id
                        ).then(result => {
                            if (result) {
                                let bill_id =
                                    result.soap && size(result.soap.invoices)
                                        ? result.soap.invoices[0].id
                                        : result.invoice
                                        ? result.invoice.id
                                        : result.estimate
                                        ? result.estimate.id
                                        : null;
                                if (bill_id) {
                                    this.data.bill_to_invoice =
                                        find(this.billings, {
                                            id: bill_id
                                        }) || this.data.bill_to_invoice;
                                }
                            }
                        });
                    } else {
                        if (this.check_in_billing === 3) {
                            this.data.bill_to_invoice =
                                this.invoices.length > 0
                                    ? find(this.billings, {
                                          id: this.invoices[0].id
                                      })
                                    : -1;
                        }
                    }
                });
            });
        },
        billingNote(bill) {
            return (
                (bill.type === -2 ? "Estimate" : "Invoice") +
                " #" +
                bill.index +
                " (" +
                (bill.type === -2
                    ? this._toDateTime(bill.start.date).toFormat(
                          this._dateFormat
                      )
                    : this._toDateTime(bill.start.date).toFormat(
                          this._dateFormat
                      )) +
                ")"
            );
        },
        async billingSoapSave() {
            let self = this;
            let bs_response = false;
            self.loading = true;
            self.saveButton = false;

            if (self.data.params.treatment.appointment_id) {
                // create soap by soap_id
                if (self.data.params.treatment.soap_id) {
                    self.data.soap = self.data.params.treatment.soap_id;
                } else {
                    if (self.data.soap) {
                        let soapData = {
                            isShortcut: 1,
                            template: self.data.soap,
                            note_date: self.data.time.toFormat(
                                self._dateFormat
                            ),
                            note_time: self.data.time.toFormat("hh:mm a"),
                            isShortcut_pid: self.data.params.patient.id
                        };

                        let response = await $.post(
                            process.env.APPS + "index.php/patient/soap/save",
                            soapData
                        );

                        if (response.status === "0") {
                            if (response.plain_id) {
                                self.data.soap = response.plain_id;
                            }
                        }
                        bs_response = true;
                    }
                }

                // create invoice/estimate by billing_to_invoice
                if (
                    self.data.bill_to_invoice === -1 ||
                    self.data.bill_to_invoice === -2
                ) {
                    let status = 1;

                    // --- marked for new estimate
                    if (self.data.bill_to_invoice === -2) {
                        status = 0;
                    }

                    let billingData = {
                        cid: self.data.params.clientID,
                        client: "Fake data",
                        pid: self.data.params.patient.id,
                        location_id: 1,
                        start: self.data.time.toFormat(self._dateFormat),
                        start_time: self.data.time.toFormat("hh:mm a"),
                        expire: self.data.time
                            .plus({ days: 14 })
                            .toFormat(self._dateFormat),
                        soap_id: self.data.soap,
                        status: status,
                        title: status ? "Invoice" : "Estimate",
                        template: 0,
                        treatmentBoard: 1
                    };

                    let billingResponse = await $.post(
                        process.env.APPS + "index.php/billing/invoice/save",
                        billingData
                    );

                    if (billingResponse.plain_id) {
                        if (billingData.status === 1) {
                            self.data.isInvoice = 1;
                            self.data.invoice_id = billingResponse.plain_id;
                        } else {
                            self.data.isInvoice = 0;
                            self.data.estimate_id = billingResponse.plain_id;
                        }
                    }
                } else {
                    if (self.data.bill_to_invoice.status) {
                        self.data.isInvoice = 1;
                        self.data.invoice_id = self.data.bill_to_invoice.id;
                    } else {
                        self.data.isInvoice = 0;
                        self.data.estimate_id = self.data.bill_to_invoice.id;
                    }
                }
            } else {
                // create soap by soap_id
                if (self.data.soap) {
                    let soapData = {
                        isShortcut: 1,
                        template: self.data.soap,
                        note_date: self.data.time.toFormat(self._dateFormat),
                        note_time: self.data.time.toFormat("hh:mm a"),
                        isShortcut_pid: self.data.params.patient.id
                    };

                    let response = await $.post(
                        process.env.APPS + "index.php/patient/soap/save",
                        soapData
                    );

                    if (response.status === "0") {
                        if (response.plain_id) {
                            self.data.soap = response.plain_id;
                        }
                    }
                }

                // create invoice/estimate by billing_to_invoice
                if (
                    self.data.bill_to_invoice === -1 ||
                    self.data.bill_to_invoice === -2
                ) {
                    let status = 1;

                    // --- marked for new estimate
                    if (self.data.bill_to_invoice === -2) {
                        status = 0;
                    }

                    let billingData = {
                        cid: self.data.params.clientID,
                        client: "Fake data",
                        pid: self.data.params.patient.id,
                        location_id: 1,
                        start: self.data.time.toFormat(self._dateFormat),
                        start_time: self.data.time.toFormat("hh:mm a"),
                        expire: self.data.time
                            .plus({ days: 14 })
                            .toFormat(self._dateFormat),
                        soap_id: self.data.soap,
                        status: status,
                        title: status ? "Invoice" : "Estimate",
                        template: 0,
                        treatmentBoard: 1
                    };

                    let billingResponse = await $.post(
                        process.env.APPS + "index.php/billing/invoice/save",
                        billingData
                    );

                    if (billingResponse.plain_id) {
                        if (billingData.status === 1) {
                            self.data.isInvoice = 1;
                            self.data.invoice_id = billingResponse.plain_id;
                        } else {
                            self.data.isInvoice = 0;
                            self.data.estimate_id = billingResponse.plain_id;
                        }
                    }
                    bs_response = true;
                } else {
                    if (self.data.bill_to_invoice.status) {
                        self.data.isInvoice = 1;
                        self.data.invoice_id = self.data.bill_to_invoice.id;
                    } else {
                        self.data.isInvoice = 0;
                        self.data.estimate_id = self.data.bill_to_invoice.id;
                    }
                }
            }
            return bs_response;
        },
        async save_bundle(post, bundle_data) {
            let self = this;
            if (bundle_data.selectedElements) {
                bundle_data.selectedElements.forEach(item => {
                    post["fm_bundle_element_" + item.id] = "on";
                    post["min_qty_" + item.id] = item.quantity;
                    if (item.max_quantity) {
                        post["max_qty_" + item.id] = item.max_quantity;
                    }

                    if (item.send_to_lab_value) {
                        post["fm_bundle_element_send_to_lab_" + item.id] = "on";
                    }

                    if (item.tag_number) {
                        post["fm_bundle_element_tag_number_" + item.id] =
                            item.tag_number;
                    }
                });
            }
            return await $.post(
                process.env.APPS + "index.php/billing/procedure/save",
                post
            );
        },
        async save() {
            let self = this;
            let response = false;
            let valid = await self.$validator.validateAll();

            if (
                _.find(self.data.staffs, { id: self.data.provider_id }) ===
                undefined
            ) {
                if (self.data.staffs.length > 0) {
                    let currentStaff = _.sortBy(
                        self.data.staffs.map(staff => {
                            return _.find(self.providers, {
                                id: staff.id
                            });
                        }),
                        ["name"]
                    );
                    if (currentStaff[0] !== undefined) {
                        self.data.provider_id = currentStaff[0].id;
                    } else {
                        self.data.provider_id = _.sortBy(self.data.staffs, [
                            "fullname"
                        ])[0].id;
                    }
                }
            }
            if (valid) {
                let bs_response = await this.billingSoapSave();
                let params = {
                    appointment_id: self.data.params.treatment.appointment_id,
                    start: parseInt(
                        self.data.time.set({ second: 0 }).toSeconds()
                    ),
                    start_record_time: self.data.time.toFormat("hh:mm: a"),
                    start_record_date: self.data.time.toLocaleString(),
                    admitted_for: self.data.params.treatment.admitted_for,
                    patient_id: self.data.params.patient.id,
                    client_id: self.data.params.clientID,
                    staff_id: self.data.staffs.map(staff => staff.id),
                    template_id: self.data.template_id,
                    room_id: self.data.room,
                    status_id: self.TREATMENT_STATUS.TREATMENT,
                    invoice_id: self.data.invoice_id
                        ? self.data.invoice_id
                        : null,
                    estimate_id: self.data.estimate_id
                        ? self.data.estimate_id
                        : null,
                    soap_id: self.data.soap,
                    activities: filter(self.templates, { selected: true }),
                    triage_type_id: self.data.triage_type_id
                };

                try {
                    if (params) {
                        response = await this.$http
                            .put(
                                process.env.BARRACUDA +
                                    "schedule/treatment-board/" +
                                    self.data.params.treatmentID,
                                params
                            )
                            .then(async response => {
                                if (response.status === 200) {
                                    if (
                                        self.bundle_data &&
                                        self.bundle_data.bundle_id
                                    ) {
                                        let post = {
                                            pid: self.data.params.patient.id,
                                            bid: self.data.provider_id,
                                            soap_id:
                                                self.data.soap !== -1
                                                    ? self.data.soap
                                                    : null,
                                            template: self.data.template_id,
                                            bill_to_invoice: self.data.isInvoice
                                                ? self.data.invoice_id
                                                : self.data.estimate_id,
                                            start: self.data.time.toFormat(
                                                self._dateFormat
                                            ),
                                            start_time: self.data.time.toFormat(
                                                "hh:mm a"
                                            ),
                                            status: self.data.isInvoice
                                        };
                                        let bundleResponse = [];
                                        bundleResponse = JSON.parse(
                                            await self.save_bundle(
                                                post,
                                                self.bundle_data
                                            )
                                        );

                                        if (
                                            bundleResponse.accession_id &&
                                            bundleResponse.antech_token
                                        ) {
                                            let labResponse = {};

                                            if (
                                                (bundleResponse.lab_ids &&
                                                    bundleResponse.lab_ids
                                                        .length > 0) ||
                                                bundleResponse.idexx_lab_id
                                            ) {
                                                labResponse = await this.getLabIntegrationObject(
                                                    bundleResponse,
                                                    "appointment"
                                                );
                                            }

                                            self.$root.$emit(
                                                "call-antech-iframe",
                                                {
                                                    antech_token:
                                                        bundleResponse.antech_token,
                                                    accession_id:
                                                        bundleResponse.accession_id,
                                                    lab_response: labResponse
                                                }
                                            );
                                        } else if (
                                            (bundleResponse.lab_ids &&
                                                bundleResponse.lab_ids.length >
                                                    0) ||
                                            bundleResponse.idexx_lab_id
                                        ) {
                                            let lab_response = await this.getLabIntegrationObject(
                                                bundleResponse,
                                                "appointment"
                                            );
                                            if (lab_response.idexx_iframe) {
                                                //load idexx iframe
                                                self.$root.$emit(
                                                    "call_idexx_iframe",
                                                    lab_response
                                                );
                                            } else {
                                                //load requisition form modal
                                                self.$root.$emit(
                                                    "call-multirequsition-modal",
                                                    lab_response
                                                );
                                            }
                                        }
                                    }
                                    if (
                                        self.data.params.treatment
                                            .appointment_id
                                    ) {
                                        this.$http.post(
                                            process.env.BARRACUDA +
                                                "schedule/appointment/" +
                                                self.data.params.treatment
                                                    .appointment_id +
                                                "/status",
                                            {
                                                status_id: 17,
                                                note: "Treatment started",
                                                room_id: self.data.room,
                                                invoice_id:
                                                    self.data.bill_to_invoice
                                                        .type === -1
                                                        ? self.data
                                                              .bill_to_invoice
                                                              .id
                                                        : null,
                                                estimate_id:
                                                    self.data.bill_to_invoice
                                                        .type === -2
                                                        ? self.data
                                                              .bill_to_invoice
                                                              .id
                                                        : null
                                            }
                                        );
                                        this.$socket.client.emit(
                                            "v-broadcast-all",
                                            {
                                                action: "refresh",
                                                businessId: this._setting
                                                    .business_id,
                                                instance: this.uniqid(), //random
                                                params
                                            }
                                        );
                                    }

                                    self.loading = false;
                                    self.$refs.treatementStart.hide();
                                    self.$root.$emit(
                                        "treatment-board-refresh",
                                        {
                                            params: response.data.response
                                        }
                                    );

                                    if (bs_response) {
                                        self.$socket.client.emit(
                                            "v-broadcast-all",
                                            {
                                                action: "refresh-document",
                                                patient_id:
                                                    self.data.params.patient.id,
                                                businessId:
                                                    self._setting.business_id
                                            }
                                        );
                                    } else if (self.isRelatedToTreatmentBoard) {
                                        self.$socket.client.emit(
                                            "v-broadcast",
                                            {
                                                action: "refresh-treatment",
                                                businessId:
                                                    self._setting.business_id
                                            }
                                        );
                                    }

                                    self._message(
                                        "Treatment started",
                                        "success"
                                    );
                                } else {
                                    self.loading = false;
                                    self._message(
                                        "Error saving data",
                                        "danger"
                                    );
                                }
                            });
                    }
                } catch (errors) {
                    self.loading = false;
                    self._message("Error saving data", "danger");
                }
            }
        },
        async getTriageList() {
            this.loadingOption.triage = true;
            await TriageService.getTriageTypeLists()
                .then(res => {
                    if (res && res.code === 200) {
                        this.data.triage_type_id =
                            this.data?.params?.treatment?.active_triage
                                ?.triage_type_id ?? this.data.triage_type_id;
                        this.triageOptions = res.resources;
                        this.loadingOption.triage = false;
                    } else {
                        throw new Error(
                            "Error: Unable to get the triage list."
                        );
                    }
                })
                .catch(e => {
                    console.error(e);
                    this._message(
                        "Error: Unable to get the triage list.",
                        "danger"
                    );
                })
                .finally(() => {});
        }
    }
};
</script>
